import { Loader2, Plus, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SegmentCard } from "./SegmentCard";

import EmptyState from "@/components/ui/v2/empty-state";
import { ROUTES } from "@/constants/routes";
import type { SegmentStatus } from "@/features/segments/constants";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { useLoadMoreSentinel } from "@/lib/hooks/useLoadMoreSentinel";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { cn } from "@/lib/utils/cn";

type SegmentListItem = {
  id: string;
  name: string;
  status: SegmentStatus;
  lastUpdatedDate?: string;
};

type SegmentListProps = {
  segments: SegmentListItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  isLoading: boolean;
  isFetching: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
};

export function SegmentList({
  segments,
  selectedId,
  onSelect,
  isLoading,
  isFetching,
  hasMore,
  onLoadMore,
  isLoadingMore,
}: SegmentListProps) {
  const { t } = useTranslation();
  const { canCreate } = usePermissionMatrix();
  const sentinelRef = useLoadMoreSentinel({
    hasMore,
    isLoadingMore,
    onLoadMore,
  });

  const firstCardRef = useTourTarget<HTMLButtonElement>(SEGMENT_TOUR_TARGET.OVERVIEW_SEGMENT);
  const firstStatusRef = useTourTarget<HTMLSpanElement>(SEGMENT_TOUR_TARGET.OVERVIEW_STATUS);
  const isRefetching = isFetching && !isLoading && !isLoadingMore;

  return (
    <div
      className={cn(
        "space-y-2 transition-opacity duration-200",
        isRefetching && "opacity-60",
      )}
    >
      {!isLoading && segments.length === 0 && (
        <div className="flex min-h-48 items-center justify-center">
          <EmptyState
            icon={SearchX}
            title={t("common.noSegments", "No segments found")}
            description={
              canCreate("segments")
                ? t(
                  "common.segmentsNoDataDescription",
                  "Get started by creating a segment.",
                )
                : t(
                  "common.segmentsNoDataDescriptionReadonly",
                  "There are no segments yet.",
                )
            }
            action={
              canCreate("segments")
                ? {
                  label: `${t("common.add")} ${t("common.segment")}`,
                  href: ROUTES.new,
                  icon: <Plus className="mr-1.5 size-4" />,
                }
                : undefined
            }
          />
        </div>
      )}

      {segments.map((segment, index) => (
        <SegmentCard
          cardRef={index === 0 ? firstCardRef : undefined}
          statusRef={index === 0 ? firstStatusRef : undefined}
          key={segment.id}
          name={segment.name}
          status={segment.status}
          lastUpdatedDate={segment.lastUpdatedDate}
          isSelected={segment.id === selectedId}
          onClick={() => onSelect(segment.id)}
        />
      ))}

      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-2">
          {isLoadingMore && (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
    </div>
  );
}
